import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { twoFactorService } from '../../services/api';
import { INK, MUTED, RULE, Section } from '../analytics/analyticsUi';

const GREEN = '#1BAA52';
const formatDay = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const digitsOnly = (value) => value.replace(/\D/g, '').slice(0, 6);

const CodeField = ({ value, onChange, label = '6-digit code from the app', allowRecovery = false, autoFocus = true }) => (
  <TextField
    label={allowRecovery ? 'Code from the app, or a recovery code' : label}
    value={value}
    onChange={(e) => onChange(allowRecovery ? e.target.value : digitsOnly(e.target.value))}
    autoComplete="one-time-code"
    autoFocus={autoFocus}
    size="small"
    fullWidth
    inputProps={allowRecovery
      ? { autoCapitalize: 'none', spellCheck: false }
      : { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { letterSpacing: '0.3em', fontSize: '1.1rem' } }}
  />
);
const codeReady = (code, allowRecovery) => (allowRecovery ? code.trim().length >= 6 : code.length === 6);

// Recovery codes with copy and download; shown once after they are created
const RecoveryCodes = ({ codes, email }) => {
  const [copied, setCopied] = useState(false);
  const text = `Recovery codes for ${email} (WhatsApp API Portal)\nEach code works once.\n\n${codes.join('\n')}\n`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { /* clipboard unavailable; the codes are on screen */ }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'whatsapp-portal-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <Box>
      <Box
        component="ul"
        aria-label="Recovery codes"
        sx={{
          listStyle: 'none', m: 0, p: 2, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1,
          bgcolor: '#F6F8FA', border: `1px solid ${RULE}`, borderRadius: 1.5,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.95rem', color: INK
        }}
      >
        {codes.map((code) => <li key={code}>{code}</li>)}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copy} sx={{ textTransform: 'none' }}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={download} sx={{ textTransform: 'none' }}>
          Download
        </Button>
      </Box>
    </Box>
  );
};

const TwoFactorSection = ({ email }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // dialog: null | 'setup' | 'codes' | 'disable' | 'regenerate'
  const [dialog, setDialog] = useState(null);
  const [setup, setSetup] = useState(null); // { qrCode, secret, issuer }
  const [showKey, setShowKey] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const recoveryNotice = location.state?.recoveryCodesLeft;

  const load = async () => {
    try {
      setLoadError(null);
      setStatus((await twoFactorService.status()).data);
    } catch (err) {
      setLoadError('Two-step verification settings could not be loaded.');
    }
  };
  useEffect(() => { load(); }, []);

  const close = () => {
    if (working) return;
    setDialog(null);
    setCode('');
    setPassword('');
    setError(null);
    setShowKey(false);
    if (dialog === 'codes') setCodes(null);
  };

  const run = async (action) => {
    try {
      setWorking(true);
      setError(null);
      await action();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setWorking(false);
    }
  };

  const startSetup = () => run(async () => {
    const response = await twoFactorService.setup();
    setSetup(response.data);
    setCode('');
    setDialog('setup');
  });

  const confirmSetup = () => run(async () => {
    const response = await twoFactorService.enable(code);
    setCodes(response.data.recoveryCodes);
    setCode('');
    setDialog('codes');
    await load();
  });

  const turnOff = () => run(async () => {
    await twoFactorService.disable(password, code.trim());
    setDialog(null);
    setCode('');
    setPassword('');
    await load();
  });

  const regenerate = () => run(async () => {
    const response = await twoFactorService.regenerateRecoveryCodes(code.trim());
    setCodes(response.data.recoveryCodes);
    setCode('');
    setDialog('codes');
    await load();
  });

  const enabled = status?.enabled;

  return (
    <Section
      title="Two-step verification"
      description="Sign in with your password and a code from an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or Authy."
    >
      {recoveryNotice !== undefined && (
        <Alert severity={recoveryNotice <= 3 ? 'warning' : 'info'} sx={{ mb: 2 }}>
          You signed in with a recovery code. {recoveryNotice} {recoveryNotice === 1 ? 'code is' : 'codes are'} left
          {recoveryNotice <= 3 ? '; create new recovery codes below.' : '.'}
        </Alert>
      )}
      {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{loadError}</Alert>}

      {!status && !loadError ? (
        <Skeleton width={260} height={32} />
      ) : status && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: enabled ? GREEN : '#8A94A6' }} />
              <Typography sx={{ color: INK, fontWeight: 600 }}>{enabled ? 'On' : 'Off'}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
              {enabled
                ? `Turned on ${formatDay(status.enabledAt)}. ${status.recoveryCodesLeft} of 10 recovery codes left.${status.required ? ' Required for administrators.' : ''}`
                : 'Anyone with your password can sign in.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {enabled ? (
              <>
                <Button variant="outlined" onClick={() => { setError(null); setDialog('regenerate'); }} sx={{ textTransform: 'none' }}>
                  New recovery codes
                </Button>
                {!status.required && (
                  <Button color="error" onClick={() => { setError(null); setDialog('disable'); }} sx={{ textTransform: 'none' }}>
                    Turn off
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                onClick={startSetup}
                disabled={working}
                startIcon={working && !dialog ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none' }}
              >
                Set up two-step verification
              </Button>
            )}
          </Box>
        </Box>
      )}
      {error && !dialog && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Setup: scan, then confirm a code */}
      <Dialog open={dialog === 'setup'} onClose={close} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Set up two-step verification</DialogTitle>
        <DialogContent>
          <Box component="ol" sx={{ pl: 2.5, m: 0, color: INK, '& > li': { mb: 2.5 } }}>
            <li>
              <Typography variant="body2">Install an authenticator app on your phone if you don't have one.</Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1.5 }}>In the app, add an account and scan this QR code.</Typography>
              {setup && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Box component="img" src={setup.qrCode} alt="QR code to add this account to an authenticator app" sx={{ width: 180, height: 180, border: `1px solid ${RULE}`, borderRadius: 1.5 }} />
                  <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
                    {showKey ? (
                      <>
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Setup key (time based)</Typography>
                        <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', color: INK, wordBreak: 'break-all' }}>{setup.secret}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5 }}>Account: {setup.account}</Typography>
                      </>
                    ) : (
                      <Link component="button" type="button" variant="body2" onClick={() => setShowKey(true)} sx={{ color: '#0D7332', fontWeight: 600 }}>
                        Can't scan it? Enter a key instead
                      </Link>
                    )}
                  </Box>
                </Box>
              )}
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Enter the 6-digit code the app shows for {setup?.issuer || 'this portal'}.</Typography>
              <Box sx={{ maxWidth: 260 }}>
                <CodeField value={code} onChange={setCode} autoFocus={false} />
              </Box>
            </li>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmSetup}
            disabled={working || !codeReady(code)}
            startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            {working ? 'Checking…' : 'Turn on'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recovery codes, shown once */}
      <Dialog open={dialog === 'codes'} onClose={close} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Save your recovery codes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
            If you lose your phone, sign in with one of these codes instead. Each works once. Keep them somewhere safe, such as a
            password manager; they won't be shown again.
          </Typography>
          {codes && <RecoveryCodes codes={codes} email={email} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={close} sx={{ textTransform: 'none' }}>I've saved them</Button>
        </DialogActions>
      </Dialog>

      {/* Turn off */}
      <Dialog open={dialog === 'disable'} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Turn off two-step verification?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
            Your account will be protected by your password only. Confirm with your password and a current code.
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <input type="text" name="username" autoComplete="username" value={email || ''} readOnly hidden />
            <TextField label="Password" type="password" size="small" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            <CodeField value={code} onChange={setCode} allowRecovery autoFocus={false} />
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={working} sx={{ textTransform: 'none' }}>Keep it on</Button>
          <Button
            variant="contained"
            color="error"
            onClick={turnOff}
            disabled={working || !password || !codeReady(code, true)}
            startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            Turn off
          </Button>
        </DialogActions>
      </Dialog>

      {/* New recovery codes */}
      <Dialog open={dialog === 'regenerate'} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Create new recovery codes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
            Your old recovery codes stop working. Enter a current code from the app to continue.
          </Typography>
          <CodeField value={code} onChange={setCode} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={regenerate}
            disabled={working || !codeReady(code)}
            startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            Create new codes
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
};

export default TwoFactorSection;
